import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Product }      from '../types';
import { fetchProducts } from '../utils/api';
import { CATEGORIES, WHATSAPP_URL } from '../utils/constants';
import ProductCard  from '../components/ProductCard';
import SkeletonGrid from '../components/SkeletonGrid';
import AISearchBar  from '../components/AISearchBar';
import SEOHead from '../components/SEOHead';
import './Home.css';

const Home: React.FC = () => {
  const [featured,    setFeatured]    = useState<Product[]>([]);
  const [newArrivals, setNewArrivals] = useState<Product[]>([]);
  const [loading,     setLoading]     = useState<boolean>(true);
  const [loadError,   setLoadError]   = useState<boolean>(false);
  const navigate = useNavigate();

  const loadProducts = (): void => {
    setLoading(true);
    setLoadError(false);
    Promise.all([
      fetchProducts({ in_stock: true, sort: 'price_desc' }),
      fetchProducts({ in_stock: true, sort: 'newest'     }),
    ]).then(([feat, newArr]) => {
      setFeatured((feat.products ?? []).slice(0, 8));
      setNewArrivals((newArr.products ?? []).slice(0, 8));
    }).catch(() => {
      setLoadError(true);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadProducts(); }, []); // runs once on mount

  const marqueeItems = ['New Arrivals', '✦', 'Kurta Sets', '✦', 'Palazzo Sets', '✦', 'Velvet Collection', '✦', 'Ethnic Wear', '✦', 'Free Shipping', '✦', 'WhatsApp Orders', '✦'];

  return (
    <div className="home">
      <SEOHead
        title="Indian Ethnic Wear — Kurtas, Suits, Palazzo Sets"
        description="Discover beautiful Indian women's ethnic wear at Balaji Fashion House. Shop kurtas, suits, palazzo sets, velvet collection and more. WhatsApp enquiry available."
        keywords="Indian ethnic wear, kurta for women, palazzo set, salwar kameez, velvet kurta, woolen suit, Balaji Fashion House, ethnic wear online India"
        url="/"
        type="website"
      />
      {/* Marquee Strip */}
      <div className="marquee-strip">
        <div className="marquee-content">
          {[...marqueeItems, ...marqueeItems].map((item, i) => (
            <span key={i} className={item === '✦' ? 'marquee-sep' : 'marquee-item'}>{item}</span>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-content container">
          <div className="hero-badge">
            <span>🇮🇳</span> Indian Ethnic Wear
          </div>
          <h1 className="hero-title">
            Elegance in Every<br /><span>Thread & Weave</span>
          </h1>
          <p className="hero-subtitle">
            Discover our exquisite collection of handpicked kurtas, suits,<br />
            palazzo sets & more — crafted for the modern Indian woman.
          </p>
          <div className="hero-actions">
            <button className="btn-primary hero-btn" onClick={() => navigate('/products')}>
              Explore Collections
            </button>
            <a href={WHATSAPP_URL('Hi! I\'d like to enquire about your collection.')}
               target="_blank" rel="noreferrer" className="hero-btn-outline">
              💬 WhatsApp Enquiry
            </a>
          </div>
          <div className="hero-stats">
            <div className="stat"><span>196+</span><label>Products</label></div>
            <div className="stat"><span>10+</span><label>Categories</label></div>
          </div>
        </div>
      </section>

      {/* AI Search */}
      <section className="ai-search-section container">
        <div className="ai-search-card">
          <div className="ai-search-label">🤖 AI-Powered Search</div>
          <h2>Find Your Perfect Outfit</h2>
          <p>Try: "Show me red velvet kurtas for wedding" or "Palazzo set under ₹1200"</p>
          <AISearchBar />
        </div>
      </section>

      {/* Categories */}
      <section className="section container">
        <div className="ornament"><span>✦</span></div>
        <h2 className="section-title">Shop by Category</h2>
        <p className="section-subtitle">Curated collections for every occasion</p>
        <div className="section-divider" />
        <div className="categories-grid">
          {CATEGORIES.map(cat => (
            <Link key={cat.name} to={`/products?category=${encodeURIComponent(cat.name)}`}
              className="category-card">
              <div className="cat-emoji">{cat.emoji}</div>
              <div className="cat-name">{cat.name}</div>
              <div className="cat-desc">{cat.desc}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container">
          <div className="ornament"><span>✦</span></div>
          <h2 className="section-title">Featured Collection</h2>
          <p className="section-subtitle">Our most loved & bestselling pieces</p>
          <div className="section-divider" />
          {loading
            ? <SkeletonGrid />
            : loadError
            ? <div className="home-load-error">
                <p>Could not load products.</p>
                <button onClick={loadProducts} className="btn-primary">Try Again</button>
              </div>
            : <div className="products-grid">{featured.map(p => <ProductCard key={p.id} product={p} />)}</div>
          }
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <button className="btn-gold" onClick={() => navigate('/products')}>
              View All Collections →
            </button>
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      <section className="new-arrivals-section">
        <div className="container">
          <div className="ornament" style={{ '--gold': '#f7c381' } as React.CSSProperties}><span>✦</span></div>
          <h2 className="section-title">New Arrivals</h2>
          <p className="section-subtitle" style={{ color: 'rgba(247,195,129,0.6)' }}>Fresh styles just added to our collection</p>
          <div className="section-divider" />
          {loading
            ? <SkeletonGrid />
            : loadError
            ? <div className="home-load-error dark">
                <p>Could not load products.</p>
                <button onClick={loadProducts} className="btn-primary">Try Again</button>
              </div>
            : <div className="products-grid">{newArrivals.map(p => <ProductCard key={p.id} product={p} dark />)}</div>
          }
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="whatsapp-banner container">
        <div className="wa-banner-inner">
          <div>
            <h3>💬 Have a Question?</h3>
            <p>Chat with us on WhatsApp for instant assistance, custom orders & bulk enquiries</p>
          </div>
          <a href={WHATSAPP_URL('Hi! I have an enquiry about Balaji Fashion House products.')}
             target="_blank" rel="noreferrer" className="btn-whatsapp" style={{ fontSize: '14px', padding: '14px 28px' }}>
            💬 Chat Now on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
